import React from "react";
import axios from "axios";
import validator from 'validator';
import { notifications } from '@mantine/notifications';
import { Box, Group, Drawer, Button, Select, Text, useMantineTheme, TextInput, SegmentedControl, ActionIcon } from '@mantine/core';
import { IconArrowLeft } from '@tabler/icons-react';

import { useLoginContext } from "../../context/loginContext";
import { BE_API_ONE, utils } from "../../utils/globalVariables";
import { regexNumberOnly, regexStringAlphaNumStripeOnlyUpperCase, regexDecimal, numberWithComma } from "../../utils/globalFunctions";

import InputComponent from "../../components/general/InputComponent";
import InputWrapperCustom from "../../components/general/InputWrapperCustom";

import ModalResponse from "../../components/general/ModalResponse";

const DiscountSpecialEdit = ({ stateShow, fnShow, stateData, fnSuccessEdit }) => {

  const mountedRef = React.useRef(true);
  const theme = useMantineTheme();
  const { loginContext, handleSetLoginContext } = useLoginContext();

  const notificationId = React.useRef({
    errorSubmitData: 'DiscountSpecialEdit-1',
    successSubmitData: 'DiscountSpecialEdit-2',
  })

  const [data, setData] = React.useState({
    submitLoading: false,

    diskonId: '',

    diskonCodePrefix: '',
    diskonCodePrefixError: '',

    diskonDeductedFrom: utils.discountDeductedFrom.jasa.value,
    diskonPercentRupiahType: utils.discountType.percent.value,

    diskonPercentRupiahValue: '',
    diskonPercentRupiahError: '',

    diskonIsActive: '1',

    modalResponse: {
      show: false,
      type: utils.modalResponse.warning,
      hideOnClickOutSide: false,
      content: ''
    }
  });

  React.useEffect(() => {
    setData(d => ({
      ...d,
      diskonId: stateData.diskon_id,
      diskonCodePrefix: stateData.diskon_code_prefix,
      diskonDeductedFrom: stateData.diskon_deducted_from,
      diskonPercentRupiahType: stateData.diskon_type,
      diskonPercentRupiahValue: Number(stateData.diskon_value),
      diskonIsActive: stateData.diskon_is_active,
    }))
  }, [stateData])

  React.useEffect(() => {
    return () => {
      mountedRef.current = false;
    }
  }, [])

  const editData = () => {
    setData(d => ({
      ...d,
      diskonCodePrefixError: '',
      diskonPercentRupiahError: ''
    }));

    if (validator.isEmpty(data.diskonCodePrefix, { ignore_whitespace: true })) {
      setData(d => ({ ...d, diskonCodePrefixError: 'Kolom wajib diisi.' }));
    } else if (!validator.isLength(data.diskonCodePrefix, { min: 3, max: 20 })) {
      setData(d => ({ ...d, diskonCodePrefixError: 'Minimal 3 digit dan maximal 20 digit.' }));
    } else if (validator.isEmpty(String(data.diskonPercentRupiahValue), { ignore_whitespace: true })) {
      setData(d => ({ ...d, diskonPercentRupiahError: 'Kolom wajib diisi.' }));
    } else if ((data.diskonPercentRupiahType === utils.discountType.percent.value) && (Number(data.diskonPercentRupiahValue) > 100)) {
      setData(d => ({ ...d, diskonPercentRupiahError: 'Maximal 100%' }));
    } else if ((data.diskonDeductedFrom === utils.discountDeductedFrom.jasa.value) && (data.diskonPercentRupiahType === utils.discountType.rupiah.value)) {
      setData(d => ({ ...d, diskonPercentRupiahError: 'Kombinasi hanya bisa Jasa % dan Total Rp' }));
    } else if ((data.diskonDeductedFrom === utils.discountDeductedFrom.total.value) && (data.diskonPercentRupiahType === utils.discountType.percent.value)) {
      setData(d => ({ ...d, diskonPercentRupiahError: 'Kombinasi hanya bisa Jasa % dan Total Rp' }));
    } else {
      setData(d => ({ ...d, submitLoading: true }));

      const formDT = new FormData();
      const formDTHeader = { 'Content-Type': 'application/json' }

      formDT.append('json_token', loginContext.syswebapp);
      formDT.append('id', data.diskonId);
      formDT.append('nama', data.diskonCodePrefix);
      formDT.append('deducted_from', data.diskonDeductedFrom);
      formDT.append('type', data.diskonPercentRupiahType);
      formDT.append('disc_val', data.diskonPercentRupiahValue);
      formDT.append('is_active', data.diskonIsActive);

      formDT.append('deducted_from_obj', JSON.stringify(utils.discountDeductedFrom));
      formDT.append('type_obj', JSON.stringify(utils.discountType));

      axios({
        method: 'post',
        url: BE_API_ONE + '/discount_special_edit',
        data: formDT,
        headers: formDTHeader,
      }).then((response) => {
        if (!mountedRef.current) return null;

        setData(d => ({ ...d, submitLoading: false }));

        if (response.data.flag_status === 'isset') {
          fnSuccessEdit();

          setData(d => ({
            ...d,
            diskonCodePrefix: '',
            diskonDeductedFrom: utils.discountDeductedFrom.jasa.value,
            diskonPercentRupiahType: utils.discountType.percent.value,
            diskonPercentRupiahValue: '',
          }));

          notifications.show({
            id: notificationId.current.successSubmitData,
            title: utils.notification.success.caption,
            message: 'Berhasil Edit Data.',
            color: utils.notification.success.color,
            icon: utils.notification.success.icon,
            withBorder: true,
            autoClose: true,
          })
        }

        if (response.data.flag_status === 'error') {
          notifications.show({
            id: notificationId.current.errorSubmitData,
            title: utils.notification.warning.caption,
            message: response.data.pesan,
            color: utils.notification.warning.color,
            icon: utils.notification.warning.icon,
            withBorder: true,
            autoClose: true,
          })
        }

        if (response.data.flag_status === 'duplicate-name') {
          setData(d => ({
            ...d,
            modalResponse: {
              ...d.modalResponse,
              type: utils.modalResponse.warning,
              hideOnClickOutSide: false,
              content: `Nama diskon "${response.data.suspect}" sudah pernah digunakan.`,
              show: true
            }
          }))
        }

        if (response.data.flag_status === 'duplicate-code') {
          setData(d => ({
            ...d,
            modalResponse: {
              ...d.modalResponse,
              type: utils.modalResponse.warning,
              hideOnClickOutSide: false,
              content: `Terjadi kesalahan. Mohon ulangi proses.`,
              show: true
            }
          }))
        }

        if (response.data.flag_status === 'invalid-json-token') {
          handleSetLoginContext(null);
        }

        if (response.data.flag_status === 'user-inactive') {
          handleSetLoginContext(null);
        }
      }).catch(error => {
        if (!mountedRef.current) return null;

        setData(d => ({ ...d, submitLoading: false }));

        notifications.show({
          id: notificationId.current.errorSubmitData,
          title: utils.notification.warning.caption,
          message: error.message,
          color: utils.notification.warning.color,
          icon: utils.notification.warning.icon,
          withBorder: true,
          autoClose: false,
        })
      })
    }
  }

  return (
    <React.Fragment>
      <Drawer
        opened={stateShow}
        onClose={fnShow}
        withCloseButton={false}
        position="left"
        closeOnClickOutside={true}

        title={
          <Group noWrap spacing='xs'>
            <ActionIcon
              size='lg'
              variant="light"
              radius='xl'
              color="blue"
              onClick={fnShow}
            >
              <IconArrowLeft size='1.25rem' stroke={1.5} />
            </ActionIcon>

            <Text>Edit Data Diskon</Text>
          </Group>
        }
        size='md'

        styles={() => ({
          title: {
            fontWeight: 'bold',
            textTransform: 'uppercase'
          }
        })}
      >
        <Box pt='xs' pb='md'>
          <Box mb='1rem'>
            <InputComponent
              stateId='diskonMasterAddCodePrefix'
              stateWithAsterisk={true}
              stateLabel='Nama Diskon'
              stateDescription='Nama 3-20 digit. Contoh: MARANATHA-DOSEN'
              statePlaceHolder='Nama Diskon'
              stateValue={data.diskonCodePrefix}
              stateError={data.diskonCodePrefixError}
              stateStylesInput={{ marginTop: '.75rem' }}
              fnOnChange={(e) => setData(d => ({ ...d, diskonCodePrefix: regexStringAlphaNumStripeOnlyUpperCase(e.target.value) }))}
            />
          </Box>

          <InputWrapperCustom
            stateMT="1rem"
            stateLabel='Pemotongan Diskon'
            stateWithAsterisk={true}
            stateError=''
            stateDescription=''
          >
            <Select
              value={data.diskonDeductedFrom}
              placeholder="Diskon dipotong dari"
              description=''
              onChange={(val) => setData(d => ({ ...d, diskonDeductedFrom: val }))}
              data={[
                // { value: utils.discountDeductedFrom.item.value, label: utils.discountDeductedFrom.item.label },
                { value: utils.discountDeductedFrom.jasa.value, label: utils.discountDeductedFrom.jasa.label },
                { value: utils.discountDeductedFrom.total.value, label: utils.discountDeductedFrom.total.label }
              ]}
            />
          </InputWrapperCustom>

          <Group mt='1rem' noWrap spacing='xs' position='apart'>
            <Select
              value={data.diskonPercentRupiahType}
              label="Tipe Diskon"
              placeholder="Tipe Diskon"
              withAsterisk={true}
              description=''
              onChange={(val) => setData(d => ({ ...d, diskonPercentRupiahType: val, diskonPercentRupiahValue: '' }))}
              data={[
                { value: utils.discountType.percent.value, label: utils.discountType.percent.label },
                { value: utils.discountType.rupiah.value, label: utils.discountType.rupiah.label },
              ]}
            />

            <TextInput
              id='diskonPercentRupiahValue'
              size="sm"
              withAsterisk={true}
              variant='default'
              label='Besar Diskon'
              placeholder='Besar Diskon'
              readOnly={false}
              disabled={false}
              icon={data.diskonPercentRupiahType === utils.discountType.rupiah.value ? <Text size='sm'>Rp</Text> : null}
              rightSection={data.diskonPercentRupiahType === utils.discountType.percent.value ? <Text size='sm' color='dimmed'>%</Text> : null}

              value={data.diskonPercentRupiahType === utils.discountType.percent.value ? data.diskonPercentRupiahValue : numberWithComma(data.diskonPercentRupiahValue)}

              onChange={(e) => setData(d => ({
                ...d,
                diskonPercentRupiahValue: d.diskonPercentRupiahType === utils.discountType.percent.value ? regexDecimal(e.target.value) : regexNumberOnly(e.target.value)
              }))}
            />
          </Group>

          {data.diskonPercentRupiahError &&
            <Text
              c={theme.colorScheme === 'light' ? theme.colors.red[6] : theme.colors.red[2]}
              size={theme.fontSizes.xs}
              mt='.25rem'
              mb='.5rem'
            >
              {data.diskonPercentRupiahError}
            </Text>
          }

          <Box mt='1rem' mb='2rem'>
            <InputWrapperCustom
              stateLabel='Status Aktif'
              stateWithAsterisk={true}
              stateError=''
              stateDescription=''
            >
              <SegmentedControl
                fullWidth
                value={data.diskonIsActive}
                onChange={(val) => setData(d => ({ ...d, diskonIsActive: val }))}
                data={[
                  { label: 'Aktif', value: '1' },
                  { label: 'Non Aktif', value: '0' },
                ]}
              />
            </InputWrapperCustom>
          </Box>

          <Group position='center' noWrap my='xl'>
            <Button
              fullWidth
              loading={data.submitLoading}
              onClick={editData}
            >
              Submit
            </Button>
          </Group>
        </Box>
      </Drawer>

      <ModalResponse
        stateShow={data.modalResponse.show}
        fnHide={() => setData(d => ({
          ...d,
          modalResponse: {
            ...d.modalResponse,
            show: false
          }
        }))}

        stateHideOnClickOutSide={data.modalResponse.hideOnClickOutSide}
        stateType={data.modalResponse.type}
        stateContent={data.modalResponse.content}
      />

    </React.Fragment>
  )
}

export default DiscountSpecialEdit;